/**
 * BGM 音源ファイルのドロップゾーン (シナリオ編集画面の BGM セクション「アップロード」タブと、
 * 全編モーションの生成前モーダル (MotionBgmChoiceModal) で共用)。
 *
 * 見た目は ScenarioGenerator.module.css の bgmDropZone 系をそのまま使う。hidden の file input と
 * アップロード処理は呼び出し側が持ち、ここはドロップ / 「ファイルを選択」クリックを通知するだけ。
 */
import styles from './ScenarioGenerator.module.css';

export interface BgmUploadDropZoneProps {
  /** ブラウザからの音源ファイルがドロップされた */
  onFile: (file: File) => void;
  /** 「ファイルを選択」クリック (呼び出し側の hidden input を開く) */
  onChooseFile: () => void;
  /** 素材パネルからの既存素材 (application/json) のドロップ。省略時はローカルファイルとしてだけ扱う */
  onDropExistingMaterial?: (asset: { id?: unknown; contentType?: unknown }) => void;
  /** 見出し (「音源ファイルをアップロードしてください」+ 対応フォーマット) を出すか。既定 true */
  showHeading?: boolean;
}

export function BgmUploadDropZone({ onFile, onChooseFile, onDropExistingMaterial, showHeading = true }: BgmUploadDropZoneProps) {
  return (
    <div>
      {showHeading && (
        // 見出し: 何をする場所かを直接書く。
        <div style={{ marginBottom: '8px' }}>
          <div style={{ fontSize: '13px', color: 'rgba(245, 241, 230, 0.75)' }}>音源ファイルをアップロードしてください</div>
          <div style={{ fontSize: '13px', color: 'rgba(245, 241, 230, 0.4)' }}>対応フォーマット: .mp3 / .wav / .aac</div>
        </div>
      )}
      <div
        className={styles.bgmDropZone}
        data-testid="bgm-drop-zone"
        onDragOver={(e) => {
          e.preventDefault();
          // 素材パネルのカードは effectAllowed='copy' なので copy を返す (move のままだと落とせない)
          e.dataTransfer.dropEffect = 'copy';
          e.currentTarget.classList.add(styles.bgmDropZoneActive);
        }}
        onDragLeave={(e) => {
          e.currentTarget.classList.remove(styles.bgmDropZoneActive);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove(styles.bgmDropZoneActive);
          // 1) 素材パネルからの既存素材 (application/json)。音声ならそのまま BGM に紐付ける
          const json = e.dataTransfer.getData('application/json');
          if (json && onDropExistingMaterial) {
            try {
              const asset = JSON.parse(json) as { id?: unknown; contentType?: unknown };
              onDropExistingMaterial(asset);
              return;
            } catch {
              // 解析できなければローカルファイルのドロップとして扱う
            }
          }
          // 2) ブラウザからの音源ファイル → アップロード
          const file = e.dataTransfer.files?.[0];
          if (file) onFile(file);
        }}
      >
        {/* 区切り線の幅を文言 / ボタンの最大幅に合わせる (.or-divider-group) */}
        <div className="or-divider-group">
          <span className={styles.bgmDropZoneLabel}>ドラッグ＆ドロップ</span>
          <div className="or-divider"><span>または</span></div>
          <button
            type="button"
            className={styles.storyboardUploadTileButton}
            style={{ fontSize: '13px' }}
            onClick={onChooseFile}
          >
            ファイルを選択
          </button>
        </div>
      </div>
    </div>
  );
}
